import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, map, shareReplay, timer } from 'rxjs';
import { utils } from '../helper/utils';
import { User } from '../models/user.model';
import { UserRequest } from '../models/user-request.model';
import { CodeDescription } from '../models/code-description.model';
import { DownloadRequest } from '../models/download-filter.model';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  constructor(private readonly httpClient: HttpClient) {}
  private mapFilters(filters: any): any {
    if (!filters) return filters;

    const mapped: any = {};

    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) return;

      switch (key) {
        case 'userName':
          mapped['LoginName'] = value;
          break;

        case 'email':
          mapped['EmailAddress'] = value;
          break;

        case 'fullName':
          mapped['FullName'] = value;
          break;

        case 'role':
          mapped['Roles'] = Array.isArray(value) ? value.join(',') : value;
          break;

        case 'region':
          mapped['RegionCodes'] = Array.isArray(value) ? value.join(',') : value;
          break;

        case 'cluster':
          mapped['ClusterCodes'] = Array.isArray(value) ? value.join(',') : value;
          break;

        case 'shopCode':
          mapped['ShopCodes'] = Array.isArray(value) ? value.join(',') : value;
          break;

        case 'isActive':
          // Send only when a single clear choice is made (not both active+inactive)
          if (Array.isArray(value) && value.length === 1) {
            mapped['IsActive'] = value[0];
          }
          break;

        case 'applyUserFilter':
          // skip
          break;

        default:
          mapped[key] = value;
      }
    });

    return mapped;
  }
  private mapToUserView(user: User): any {
    return {
      ...user,

      id: user.userId,

      // 🔥 mappings
      shopCodes: user.maintenanceShops?.map((s) => s.maintenanceShopCode) || [],
      rolesList: user.roles || [],
      regionsList: user.regions?.map((r) => r.name) || [],
      clustersList: user.clusters ?? user.regions?.flatMap((r) => r.clusters?.map((c) => c.name) || []) ?? [],

      // 🔥 important for UI
      isActive: user.isActive,
    };
  }
  getUserList(
    page: number,
    pageLimit: number,
    sort?: string,
    searchText?: string,
    isShopUsers?: boolean,
    filters?: any, // ✅ NEW PARAM
  ): Observable<HttpResponse<User[]>> {
    // ✅ Decide endpoint based on tab
    const endpoint = isShopUsers ? 'shop-users' : 'non-shop-users';

    let url: string = `${endpoint}?page=${page}&limit=${pageLimit}`;

    if (sort) {
      url += `&sort=${encodeURIComponent(sort)}`;
    }

    if (searchText) {
      url += `&searchText=${encodeURIComponent(searchText)}`;
    }
    // ✅ APPLY FILTERS
    const mappedFilters = this.mapFilters(filters);

    if (mappedFilters) {
      Object.keys(mappedFilters).forEach((key) => {
        const value = mappedFilters[key];

        if (Array.isArray(value)) {
          value.forEach((v) => {
            url += `&${key}=${encodeURIComponent(v)}`;
          });
        } else {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
      });
    }

    return this.httpClient.get<User[]>(utils.GetApplicationSupportUrl(url), { observe: 'response' }).pipe(
      map((response: HttpResponse<User[]>) => {
        const body = response.body;

        if (body) {
          const mappedBody = body.map((item) => this.mapToUserView(item));

          return new HttpResponse({
            body: mappedBody,
            headers: response.headers,
          });
        }

        return response;
      }),
    );
  }

  private rolesCache$: Observable<CodeDescription[]> | null = null;
  private regionsCache$: Observable<CodeDescription[]> | null = null;
  private shopsCache$: Observable<CodeDescription[]> | null = null;
  public getRoles(): Observable<CodeDescription[]> {
    this.rolesCache$ ??= this.httpClient.get<CodeDescription[]>(utils.GetApplicationSupportUrl('roles')).pipe(shareReplay(1));
    return this.rolesCache$;
  }

  // ✅ REGIONS – cached with shareReplay so modal-open never triggers a fresh HTTP call
  getRegions(): Observable<CodeDescription[]> {
    this.regionsCache$ ??= this.httpClient.get<any[]>(utils.GetApplicationSupportUrl('filter/regions')).pipe(
      map((regions) => regions.map((r) => ({ code: r.code, description: r.name }))),
      shareReplay(1),
    );
    return this.regionsCache$;
  }

  // ✅ CLUSTERS (depends on region)
  getClusters(regionCodes: string): Observable<CodeDescription[]> {
    return this.httpClient
      .get<any[]>(utils.GetApplicationSupportUrl(`filter/clusters?regionCodes=${encodeURIComponent(regionCodes)}`))
      .pipe(map((clusters) => clusters.map((c) => ({ code: c.code, description: c.name }))));
  }

  // ✅ SHOPS (depends on cluster)
  getShopsByCluster(clusterCodes: string): Observable<CodeDescription[]> {
    return this.httpClient
      .get<any[]>(utils.GetApplicationSupportUrl(`filter/shops?clusterCodes=${encodeURIComponent(clusterCodes)}`))
      .pipe(map((shops) => shops.map((s) => ({ code: s.maintenanceShopCode, description: s.maintenanceShopName }))));
  }
  getShops(): Observable<CodeDescription[]> {
    this.shopsCache$ ??= this.httpClient.get<any[]>(utils.GetApplicationSupportUrl(`shops`)).pipe(
      map((shops) => shops.map((s) => ({ code: s.maintenanceShopCode, description: s.maintenanceShopName }))),
      shareReplay(1),
    );
    return this.shopsCache$;
  }
  saveUser(userRequest: UserRequest): Observable<any> {
    return this.httpClient.post<any>(utils.GetApplicationSupportUrl('add-user'), userRequest);
  }

  updateUser(userRequest: UserRequest): Observable<any> {
    return this.httpClient.post<any>(utils.GetApplicationSupportUrl('update-user'), userRequest);
  }

  public getUserDetailsBasedOnUserName(userId: number): Observable<User> {
    return this.httpClient.get<User>(utils.GetApplicationSupportUrl(`user-details/${userId}`));
  }

  deleteUser(userId: number): Observable<any> {
    if (userId > 0) {
      return this.httpClient.delete<any>(`${utils.GetApplicationSupportUrl('delete-user')}/${userId}`);
    } else {
      return timer(2000);
    }
  }

  downloadToExcel(isShopUsers: boolean, request: DownloadRequest): Observable<Blob> {
    const entityKey = isShopUsers ? 'shop-user' : 'non-shop-user';
    const url = utils.GetDownloadUrl(entityKey);
    return this.httpClient.post(url, request, { responseType: 'blob' });
  }
}
